import { useState } from 'react'
import axios from 'axios'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3003'

/**
 * Custom hook for attachment operations (preview, download)
 * @param {Object} params - Hook parameters
 * @param {string} params.projectId - Project ID
 * @param {string} params.token - Auth token
 * @param {Function} params.showInfo - Toast info callback
 * @param {Function} params.showSuccess - Toast success callback
 * @param {Function} params.showError - Toast error callback
 * @returns {Object} Hook state and methods
 */
export const useAttachmentOperations = ({ projectId, token, showInfo, showSuccess, showError }) => {
  // Attachment modal state
  const [selectedAttachment, setSelectedAttachment] = useState(null)
  const [showAttachmentModal, setShowAttachmentModal] = useState(false)

  // Download state
  const [downloadingAttachments, setDownloadingAttachments] = useState(new Set())

  /**
   * Open attachment preview modal
   */
  const handleOpenAttachment = (attachment) => {
    setSelectedAttachment(attachment)
    setShowAttachmentModal(true)
  }

  /**
   * Close attachment preview modal
   */
  const handleCloseAttachmentModal = () => {
    setShowAttachmentModal(false)
    setSelectedAttachment(null)
  }

  /**
   * Download attachment
   */
  const handleDownloadAttachment = async (attachment, e) => {
    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }

    // Get the URN from attachment - check multiple possible fields
    const documentUrn = attachment?.urn || attachment?.documentUrn || attachment?.lineageUrn || attachment?.tip || attachment?.id

    if (!documentUrn) {
      showError('Attachment information is missing')
      console.error('[Download] Attachment object:', attachment)
      return
    }

    const attachmentKey = attachment?.id || documentUrn

    if (downloadingAttachments.has(attachmentKey)) {
      return
    }

    try {
      setDownloadingAttachments(prev => new Set(prev).add(attachmentKey))
      showInfo('Preparing download...')

      // Call backend endpoint to get download URL
      const response = await axios.post(
        `${API_BASE_URL}/api/cost/${projectId}/attachments/download`,
        {
          urn: documentUrn
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          withCredentials: true
        }
      )

      if (response.data?.success && response.data.downloadUrl) {
        const downloadUrl = response.data.downloadUrl

        const link = document.createElement('a')
        link.href = downloadUrl
        link.download = attachment.name || 'download'
        link.target = '_blank'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        showSuccess(`Download started: ${attachment.name}`)
      } else {
        showError('Download URL not available')
        console.error('[Download] No URL in response:', response.data)
      }
    } catch (error) {
      console.error('[Download Error]', error)
      console.error('[Download Error] Response data:', error.response?.data)

      if (error.response) {
        const status = error.response.status
        const errorData = error.response.data
        const errorMessage = errorData?.error || errorData?.message

        if (status === 401) {
          showError('Authentication failed. Please log in again.')
        } else if (status === 403) {
          showError('You do not have permission to download this file')
        } else if (status === 404) {
          showError('File not found or no longer available')
        } else if (status === 429) {
          showError('Too many requests. Please try again later.')
        } else if (status >= 500) {
          showError('Server error. Please try again later.')
        } else {
          showError(errorMessage || `Download failed: ${status}`)
        }
      } else if (error.request) {
        showError('Network error. Please check your connection.')
      } else {
        showError('Failed to download file. Please try again.')
      }
    } finally {
      setDownloadingAttachments(prev => {
        const newSet = new Set(prev)
        newSet.delete(attachmentKey)
        return newSet
      })
    }
  }

  return {
    // State
    selectedAttachment,
    showAttachmentModal,
    downloadingAttachments,

    // Methods
    handleOpenAttachment,
    handleCloseAttachmentModal,
    handleDownloadAttachment
  }
}
